import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "yp_cart_v1";

function readGuestCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function toCartItemIn(i) {
  return { product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity };
}

function mergeCartItems(base, extra) {
  const merged = [...base];
  for (const e of extra) {
    const idx = merged.findIndex((i) => i.product_id === e.product_id && i.variant_id === e.variant_id);
    if (idx >= 0) merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + e.quantity };
    else merged.push(e);
  }
  return merged;
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(readGuestCart);
  const [ready, setReady] = useState(!user);
  // Which logged-in user's cart is currently loaded, so a re-render doesn't re-merge the
  // guest cart into the server every time — only on an actual logged-out -> logged-in edge.
  const loadedForUser = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user) {
        if (loadedForUser.current === user.id) return;
        loadedForUser.current = user.id;

        const guest = readGuestCart();
        let serverItems = [];
        try {
          serverItems = (await api.cart.get()).items || [];
        } catch { serverItems = []; }
        const merged = mergeCartItems(serverItems, guest);
        if (cancelled) return;
        setItems(merged);
        setReady(true);

        if (guest.length) {
          localStorage.removeItem(STORAGE_KEY);
          try {
            const synced = await api.cart.set(merged.map(toCartItemIn));
            if (!cancelled) setItems(synced.items || merged);
          } catch {
            // keep the locally-merged view — the next cart change will retry the sync
          }
        }
      } else {
        loadedForUser.current = null;
        if (!cancelled) {
          setItems(readGuestCart());
          setReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
    // Intentionally keyed on user?.id, not the user object, so this only re-runs on an actual
    // logged-out/in edge (see loadedForUser above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist on every change. Logged in -> best-effort server sync (fire-and-forget: the cart
  // stays optimistic/local for instant taps, the server is a sync target, not the render path).
  // Logged out -> localStorage, as before.
  useEffect(() => {
    if (!ready) return;
    if (user) {
      api.cart.set(items.map(toCartItemIn)).catch(() => {});
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on user?.id, see effect above
  }, [items, ready, user?.id]);

  const addItem = (product, variantId = null, qty = 1) => {
    const chosen = variantId || product.variants?.[0]?.variant_id;
    const variant = product.variants?.find((v) => v.variant_id === chosen);
    // Stock is only ever atomically re-checked/decremented at order time (create_order ->
    // _reserve_stock on the backend) — nothing upstream of that used to stop a 0-stock size
    // from being added and carried all the way to checkout before finally failing there. This
    // is the one place every "Add to Cart"/"Buy Now" call funnels through, so it's the safety
    // net; ProductCard/ProductDetail also disable their buttons directly for the correct UX.
    // Checked per the specific size being added, not the product as a whole — a product can
    // have one sold-out size and one in-stock size at the same time.
    const variantStock = variant?.stock ?? 0;
    if (!(variantStock > 0)) {
      toast.error(`${product.name}${variant?.label ? ` (${variant.label})` : ""} is currently out of stock.`);
      return;
    }
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.variant_id === chosen);
      if (idx >= 0) {
        const next = [...prev];
        const cappedQty = Math.min(next[idx].quantity + qty, variantStock);
        next[idx] = { ...next[idx], quantity: cappedQty, stock: variantStock };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          variant_id: chosen,
          name: product.name,
          image: (product.images || [])[0] || "/plant-placeholder.png",
          price: variant?.price ?? product.price,
          mrp: variant?.mrp ?? product.mrp,
          quantity: Math.min(qty, variantStock),
          size: variant?.label ?? "",
          stock: variantStock,
          unavailable: false,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  };

  const updateQty = (product_id, variant_id, qty) => {
    if (qty <= 0) return removeItem(product_id, variant_id);
    setItems((prev) => prev.map((i) => {
      if (i.product_id !== product_id || i.variant_id !== variant_id) return i;
      // A pre-existing localStorage cart line from before stock was tracked won't have a real
      // `stock` number — don't retroactively cap those, only lines that do carry one.
      const cap = typeof i.stock === "number" ? i.stock : qty;
      return { ...i, quantity: Math.min(qty, cap) };
    }));
  };

  const removeItem = (product_id, variant_id) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.variant_id === variant_id)));
  };

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => (i.unavailable ? s : s + i.price * i.quantity), 0);
  const shipping = subtotal >= 499 || subtotal === 0 ? 0 : 49;
  // Prices are tax-inclusive (see ProductDetail's "Inclusive of all taxes" note) — no separate
  // GST line. Matches the backend, which computes total as subtotal - discount + shipping with
  // no tax field, and the mobile app's checkout, which has never shown one.
  const total = Math.round((subtotal + shipping) * 100) / 100;

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, shipping, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
